/**
 * An interface event is a simple recreation of our datagram API, for instances where the UI
 * needs to listen for specific api events. These events are submitted after we parse the
 * datagram buffer and after we do any other handling logic, e.g. updates within the
 * handlers. However, increasingly, the addition of any special handler logic will be
 * considered an anti-pattern.
 */
type InterfaceEvent = {
  conversationCloseResponse: ConversationCloseResponseDatagram
  conversationCreateResponse: ConversationCreateResponseDatagram
  conversationDeleteResponse: ConversationDeleteResponseDatagram
  conversationPropertiesResponse: ConversationPropertiesResponseDatagram
  conversationSubscribeResponse: ConversationSubscribeResponseDatagram
  conversationTurnResponse: ConversationTurnResponseDatagram
  conversationUpdateResponse: ConversationUpdateResponseDatagram
  fileCopyResponse: FileCopyResponseDatagram
  fileCreateResponse: FileCreateResponseDatagram
  fileDeleteResponse: FileDeleteResponseDatagram
  fileDownloadResponse: FileDownloadResponseDatagram
  fileEditResponse: FileEditResponseDatagram
  fileHistoryResponse: FileHistoryResponseDatagram
  fileRevisionResponse: FileRevisionResponseDatagram
  fileMoveResponse: FileMoveResponseDatagram
  filePrepareResponse: FilePrepareResponseDatagram
  filePropertiesResponse: FilePropertiesResponseDatagram
  fileShareResponse: FileShareResponseDatagram
  filesSharedWithSelfResponse: FilesSharedWithSelfResponseDatagram
  fileUpdateResponse: FileUpdateResponseDatagram
  folderListPageResponse: FolderListPageResponseDatagram
  folderContentHistoryResponse: FolderContentHistoryResponseDatagram
  folderSubscribeResponse: FolderSubscribeResponseDatagram
  searchAutocompleteResponse: SearchAutocompleteResponseDatagram
  searchListPageResponse: SearchListPageResponseDatagram
  searchUpdateResponse: SearchUpdateResponseDatagram
  selfConversationsResponse: SelfConversationsResponseDatagram
  selfInfoResponse: SelfInfoResponseDatagram
  selfSharedFoldersResponse: SelfSharedFoldersResponseDatagram
  selfSidebarResponse: SelfSidebarResponseDatagram
  selfUpdateResponse: SelfUpdateResponseDatagram
  sharedFolderCreateResponse: SharedFolderCreateResponseDatagram
  sharedFolderFindResponse: SharedFolderFindResponseDatagram
  sharedFolderMembershipResponse: SharedFolderMembershipResponseDatagram
  userFindResponse: UserFindResponseDatagram

  didConnectAuthStatus: DidConnectAuthStatusDatagram
  didConnectAnonStatus: DidConnectAnonStatusDatagram
  didDisconnectAuthStatus: DidDisconnectAuthStatusDatagram
  didDisconnectAnonStatus: DidDisconnectAnonStatusDatagram
  wentOfflineAuthStatus: WentOfflineAuthStatusDatagram
  wentOfflineAnonStatus: WentOfflineAnonStatusDatagram
  wentOnlineAuthStatus: WentOnlineAuthStatusDatagram
  wentOnlineAnonStatus: WentOnlineAnonStatusDatagram

  afterNavigatedToConversation: InterfaceConversation
  /**
   * An event that triggers right after the file system acknowledges the intent to change to
   * a non-folder file. This is used when, for example, we are navigating to an image preview
   */
  afterNavigatedToNonFolderFile: InterfaceFile
  /**
   * An event that triggers when a user navigates to an existing search. For a new search,
   * that is kicked off by the search bar and doesn't have a materialized search object, so
   * it doesn't follow this callback and instead goes directly to the onSearchResponse
   */
  afterNavigatedToSearch: InterfaceSearch
  /**
   * An event that should trigger right after the file system acknowledges the intent to
   * change to a new folder. The path should have the new contents, though the file
   * provided might be a blank file if we are starting a fresh navigation, e.g. page load.
   * It is assumed that no file system operations have been started at all, but the navigation
   * path is assumed to be provided. The File provided as the event input might have a full
   * path and all details if the file was previously loaded, e.g. clicking on a file in the UI.
   */
  afterNavigatedToFolder: InterfaceFile
  /**
   * Expanding a folder is used in column and tree view to denote when a folder is opened but
   * the view is not switched to exclusively that folder. So the folder's contents are
   * shown, but the folder is not made into the main focus.
   */
  afterExpandedFolder: InterfaceFile
  /** This is for when a folder is closed in the tree view */
  afterShrunkFolder: InterfaceFile

  /**
   * empty message. This is for scrolling to the bottom of the agent chat, e.g. when
   * opening the agent view, when the component is mounted, etc.
   */
  scrollToLatestAgentMessage: object
  focusOnAgentInput: object
}

function EntityEventKey(type: keyof EntityEventType, entityId: string): `${keyof EntityEventType}[entityId=${string}]` {
  return `${type}[entityId=${entityId}]`
}

const AnyRequestId = Symbol("requestId")

// An event handler can take an optional event argument
// and should not return a value
type InterfaceHandler<T = unknown> = (event: T) => void
type EntityHandler<T = unknown> = (event: T, offline: boolean) => void

// An array of all currently registered event handlers for a type
type InterfaceEventHandlerList<T = unknown> = Array<InterfaceHandler<T>>
type EntityEventHandlerList<T = unknown> = Array<EntityHandler<T>>

// map of interface events
type InterfaceEventHandlerMap<E> = Map<
  string | symbol,
  InterfaceEventHandlerList<E[keyof E]>
>

// A map of event types and their corresponding event handlers.
type InterfaceEventHandlerMapByRequest<E> = Map<keyof E, InterfaceEventHandlerMap<E>>

type AllInterfaceEvents = InterfaceEventHandlerMapByRequest<InterfaceEvent>
interface InterfaceEventHandlerOptions { requestId: string | symbol }

type AllEntityEvents = Map<
  { [K in keyof EntityEventType]: `${K}[entityId=${string}]` }[keyof EntityEventType],
  { [K in keyof EntityEventType]: EntityEventHandlerList<EntityEventType[K]> }[keyof EntityEventType]
>

type AllGenericEntityEvents = Map<
  { [K in keyof EntityEventType]: K }[keyof EntityEventType],
  { [K in keyof EntityEventType]: EntityEventHandlerList<EntityEventType[K]> }[keyof EntityEventType]
>

type GenericInterfaceEventHandler<K extends keyof InterfaceEvent> = InterfaceHandler<InterfaceEvent[K]>

interface State {
  allInterfaceEvents: AllInterfaceEvents
  allEntityEvents: AllEntityEvents
  allGenericEntityEvents: AllGenericEntityEvents
}

function defaultState(): State {
  return {
    allInterfaceEvents: new Map(),
    allEntityEvents: new Map(),
    allGenericEntityEvents: new Map(),
  }
}
let state: Ref<State>

function clearState() {
  state.value = defaultState()
}

function addEntityEventListener<E extends keyof EntityEventType>(
  type: E,
  fileId: string,
  handler: EntityHandler<EntityEventType[E]>,
) {
  const key = EntityEventKey(type, fileId)
  let handlers = state.value.allEntityEvents.get(key)
  if (!handlers) {
    handlers = []
    state.value.allEntityEvents.set(key, handlers)
  }
  handlers.push(handler as any)
}

function removeEntityEventListener<E extends keyof EntityEventType>(
  type: E,
  fileId: string,
  handler: EntityHandler<EntityEventType[E]>,
) {
  const key = EntityEventKey(type, fileId)
  const handlers = state.value.allEntityEvents.get(key)
  if (!handlers) {
    // console.warn("Requested removing non-existent handler", type, fileId, handler)
    return
  }
  handlers.splice(handlers.indexOf(handler as any) >>> 0, 1)
  if (handlers.length === 0) {
    state.value.allEntityEvents.delete(key)
  }
}

function addGenericEntityEventListener<E extends keyof EntityEventType>(
  type: E,
  handler: EntityHandler<EntityEventType[E]>,
) {
  let handlers = state.value.allGenericEntityEvents.get(type)
  if (!handlers) {
    handlers = []
    state.value.allGenericEntityEvents.set(type, handlers)
  }
  handlers.push(handler as any)
}

function removeGenericEntityEventListener<E extends keyof EntityEventType>(
  type: E,
  handler: EntityHandler<EntityEventType[E]>,
) {
  const handlers = state.value.allGenericEntityEvents.get(type)
  if (!handlers) {
    return
  }
  handlers.splice(handlers.indexOf(handler as any) >>> 0, 1)
  if (handlers.length === 0) {
    state.value.allGenericEntityEvents.delete(type)
  }
}


function removeAllEntityEventListeners<E extends keyof EntityEventType>(
  type: E,
  handler: EntityHandler<EntityEventType[E]>,
) {
  for (const [key, handlers] of state.value.allEntityEvents.entries()) {
    handlers.splice(handlers.indexOf(handler as any) >>> 0, 1)
    if (handlers.length === 0) {
      state.value.allEntityEvents.delete(key)
      state.value.allGenericEntityEvents.delete(type)
    }
  }
}

function emitEntityEvent<E extends keyof EntityEventType>(
  type: E,
  entityId: string,
  event: EntityEventType[E],
  offline: boolean,
) {
  const key = EntityEventKey(type, entityId)

  const handlers = state.value.allEntityEvents.get(key)
  if (handlers) {
    for (const handler of handlers) {
      handler(event as any, offline)
    }
  }

  const genericHandlers = state.value.allGenericEntityEvents.get(type)
  if (genericHandlers) {
    for (const handler of genericHandlers) {
      handler(event as any, offline)
    }
  }
}

function addInterfaceEventListener<Key extends keyof InterfaceEvent>(
  type: Key,
  handler: GenericInterfaceEventHandler<Key>,
  options: InterfaceEventHandlerOptions = {
    requestId: AnyRequestId,
  },
) {
  if (!state.value.allInterfaceEvents.get(type))
    state.value.allInterfaceEvents.set(type, new Map())
  const handlers = state.value.allInterfaceEvents.get(type)!
  const requestHandlers = handlers.get(options.requestId)
  if (requestHandlers) {
    requestHandlers.push(handler as any)
  }
  else {
    handlers.set(options.requestId, [handler as any])
  }
}

function removeInterfaceEventListener<Key extends keyof InterfaceEvent>(
  type: Key,
  handler: GenericInterfaceEventHandler<Key>,
) {
  const handlers = state.value.allInterfaceEvents.get(type)
  if (!handlers) {
    // console.warn("Requested removing non-existent handler", type, handler)
    return
  }
  for (const key of handlers.keys()) {
    const handlerList = handlers.get(key)!
    handlerList.splice(handlerList.indexOf(handler as any) >>> 0, 1)
    if (handlerList.length === 0) {
      handlers.delete(key)
    }
  }
}

function removeInterfaceEventListenerByRequestId<Key extends keyof InterfaceEvent>(
  type: Key,
  requestId: string,
) {
  state.value.allInterfaceEvents.get(type)?.delete(requestId)
}

function emitInterfaceEvent<Key extends keyof InterfaceEvent>(
  type: Key,
  evt: InterfaceEvent[Key],
  requestId?: string,
) {
  const requestIds = requestId ? [AnyRequestId, requestId] : [AnyRequestId]
  for (const requestId of requestIds) {
    const handlers = state.value.allInterfaceEvents.get(type)?.get(requestId)
    if (handlers) {
      (handlers as InterfaceEventHandlerList<InterfaceEvent[keyof InterfaceEvent]>)
        .slice()
        .map(handler => handler(evt!))
    }
  }
}

async function waitForResponse<Key extends keyof InterfaceEvent>(
  type: Key,
  where: (response: InterfaceEvent[Key]) => boolean,
  requestId?: string,
) {
  let fulfilled = false
  let response: InterfaceEvent[Key] | undefined
  while (!fulfilled) {
    const { promise, resolve } = Promise.withResolvers<InterfaceEvent[Key]>()
    const opts = requestId ? { requestId } : undefined
    addInterfaceEventListener<Key>(type, resolve, opts)
    response = await promise
    fulfilled = where(response)
    removeInterfaceEventListener(type, resolve)
  }
  if (!isDefined(response))
    throw new Error("an error occurred waiting for a response")
  return response
}

async function waitForFirstResponse<Key extends keyof InterfaceEvent>(
  type: Key,
  requestId?: string,
) {
  return waitForResponse<Key>(
    type,
    () => true,
    requestId,
  )
}

async function waitForServerResponse<Key extends keyof InterfaceEvent>(
  type: Key,
  requestId?: string,
) {
  return waitForResponse<Key>(
    type,
    response => "offline" in response && !response.offline,
    requestId,
  )
}

export default function () {
  state = useState("events-state", defaultState)

  return {
    clearEventState: clearState,
    addEntityEventListener,
    removeEntityEventListener,
    addGenericEntityEventListener,
    removeGenericEntityEventListener,
    removeAllEntityEventListeners,
    emitEntityEvent,
    addInterfaceEventListener,
    removeInterfaceEventListener,
    removeInterfaceEventListenerByRequestId,
    emitInterfaceEvent,
    waitForResponse,
    waitForFirstResponse,
    waitForServerResponse,
  }
}
